/**
 * @file customer-order-columns.tsx
 * @description Колонки заказов клиента для `ResponsiveList` (phase19.31,
 * SC63): «Последние заказы» на обзоре и вкладка «Заказы» карточки клиента.
 *
 * Заказ (номер без «№» + дата) · Стадия (пилюля по тону) · Оплата словом ·
 * Позиций · Сумма. Номер — обычным шрифтом с табличными цифрами (SC43).
 * Компактный список: «Z-… … 175 ₽» / «22.09.2026 · Оплачен» / «стадия».
 */

import { StatusPill } from "../../ui/StatusPill";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { CustomerOrderListItemDto } from "../../../lib/api-customers";
import { ORDER_STATUS_LABEL, type OrderStatus } from "../../../lib/api-orders";
import { CUSTOMER_ORDER_PAYMENT, customerOrderTone } from "../../../lib/crm-customer-view";
import { formatRub } from "../../../lib/crm-deal-view";
import type { StatusTone } from "../../../lib/shop/status-meta";
import { formatShopDate } from "../../../lib/shop-time";

type Row = CustomerOrderListItemDto;

/**
 * Подпись стадии. У заказа без стадии сервер отдаёт код старого статуса
 * («CANCELLED») — переводим его словами; отменённый — всегда «Отменён».
 */
function stageLabel(o: Row): string {
  if (o.isCancelled) return "Отменён";
  return ORDER_STATUS_LABEL[o.stageName as OrderStatus] ?? o.stageName;
}

/** Пилюля стадии (функция разметки, не компонент: файл отдаёт только колонки). */
function stagePill(order: Row) {
  return <StatusPill tone={customerOrderTone(order)}>{stageLabel(order)}</StatusPill>;
}

/** Цвет текста оплаты по тону: «Оплачен» — зелёный, возврат — красный. */
const PAYMENT_TONE_CLASS: Partial<Record<StatusTone, string>> = {
  success: "text-[var(--status-success-fg)]",
  attention: "text-[var(--status-attention-fg)]",
  error: "text-[var(--status-error-fg)]",
};

/** Оплата словом, цветом тона (как в реестре заказов). */
function paymentText(order: Row) {
  const meta = CUSTOMER_ORDER_PAYMENT[order.paymentStatus];
  return <span data-status-tone={meta.tone} className={PAYMENT_TONE_CLASS[meta.tone] ?? "text-on-surface-variant"}>{meta.label}</span>;
}

export const CUSTOMER_ORDER_COLUMNS: ListColumn<Row>[] = [
  {
    key: "order",
    header: "Заказ",
    width: "w-[176px]",
    cell: (o) => <TwoLineCell primary={<span className="font-medium tabular-nums">{o.number}</span>} secondary={formatShopDate(o.createdAt)} />,
    compact: "title",
    compactCell: (o) => <span className="tabular-nums">{o.number}</span>,
  },
  {
    key: "stage",
    header: "Стадия",
    cell: stagePill,
    compact: "meta",
  },
  {
    key: "date",
    header: "Дата",
    compactOnly: true,
    cell: (o) => formatShopDate(o.createdAt),
    compact: "subtitle",
  },
  {
    key: "payment",
    header: "Оплата",
    width: "w-[160px]",
    cell: paymentText,
    compact: "subtitle",
  },
  {
    key: "items",
    header: "Позиций",
    width: "w-[96px]",
    align: "end",
    cell: (o) => <span className="tabular-nums">{o.itemsCount}</span>,
  },
  {
    key: "total",
    header: "Сумма",
    width: "w-[128px]",
    align: "end",
    cell: (o) => <span className="tabular-nums whitespace-nowrap">{formatRub(o.totalGross, "whole")}</span>,
    compact: "trailing",
    compactCell: (o) => <span className="type-body font-semibold tabular-nums whitespace-nowrap">{formatRub(o.totalGross, "whole")}</span>,
  },
];
